import {
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { IconClipboard, IconRefresh } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import {
  useEvDcModbusTargetExport,
  type EvDcModbusTargetExportParams,
} from "@/api/queries";
import { ErrorBanner } from "@/components/ErrorBanner";

export type EvDcModbusTargetExportModalProps = {
  opened: boolean;
  onClose: () => void;
  params: EvDcModbusTargetExportParams;
};

export function EvDcModbusTargetExportModal({
  opened,
  onClose,
  params,
}: EvDcModbusTargetExportModalProps) {
  const [copied, setCopied] = useState(false);
  const exportParams = useMemo(
    () => ({
      ...params,
      include_write_flags: false,
      limit: 200,
      offset: 0,
    }),
    [params],
  );
  const exportQuery = useEvDcModbusTargetExport(exportParams, opened);
  const json = exportQuery.data?.poller_targets_json ?? "";

  useEffect(() => {
    setCopied(false);
  }, [json, opened]);

  const handleCopy = async () => {
    if (!json) return;
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Export setup JSON" size="xl">
      <Stack>
        <Group justify="space-between">
          <Group gap="xs">
            <Badge variant="light" color="gray">
              {exportQuery.data?.env_var ?? "EMS_POLLER_EV_DC_MODBUS_TARGETS"}
            </Badge>
            <Badge variant="light" color="gray">
              control writes off
            </Badge>
          </Group>
          <Text c="dimmed" size="sm">
            {exportQuery.data?.target_count ?? 0} target
            {exportQuery.data?.target_count === 1 ? "" : "s"}
          </Text>
        </Group>

        {exportQuery.error && <ErrorBanner error={exportQuery.error} />}

        {exportQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : (
          <Textarea
            label="Setup JSON"
            description="Reviewed deployment input. This export keeps control writes off."
            value={json}
            readOnly
            autosize
            minRows={12}
            maxRows={18}
            styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
          />
        )}

        <Group justify="flex-end">
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            onClick={() => exportQuery.refetch()}
            loading={exportQuery.isFetching}
          >
            Refresh
          </Button>
          <Button leftSection={<IconClipboard size={16} />} onClick={handleCopy} disabled={!json}>
            {copied ? "Copied" : "Copy setup JSON"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
